/**
 * GpuParticleMaterial — NodeMaterial that renders GPU particles as camera-
 * facing billboard quads, reading per-instance state straight from the pool's
 * storage buffers.
 *
 * Vertex stage:
 *   1. Read pos(px,py,pz,age), vel(.w = size), misc(.x = life) for this
 *      instance from the storage attributes.
 *   2. Compute normalized life t = age/life. Dead particles (size 0) collapse
 *      to a degenerate quad → invisible, no branch needed.
 *   3. Apply a 4-stop size ramp (uniforms) → final world-space billboard size.
 *   4. Billboard: place the quad center in view space (cameraViewMatrix · world)
 *      then offset by the quad corner along the VIEW X/Y axes (camera right/up)
 *      scaled by size — the canonical view-space billboard. Project with
 *      cameraProjectionMatrix.
 *   5. RETURN the clip-space position node (the prior engine bug: a void Fn as
 *      vertexNode leaves gl_Position undefined — see VelocityPass / SplatMaterial
 *      which both `return clip`).
 *
 * Fragment stage:
 *   - 4-stop RGBA color ramp over t, multiplied by a soft round falloff from
 *     the quad UV so particles read as discs not squares (plus optional texture).
 *   - Emits STRAIGHT rgb; three premultiplies once (premultipliedAlpha = true).
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from '../../render/post/mrtOverlay.js';
import {
  Fn, uniform, vec2, vec3, vec4, float,
  positionGeometry, uv,
  cameraViewMatrix, cameraProjectionMatrix,
  varyingProperty, texture, mix, clamp, smoothstep,
} from 'three/tsl';
import type { ParticleRamp } from './types.js';
import { sixWayLitNodes, type SixWayMaps } from './SixWaySmoke.js';

export interface GpuParticleMaterialContext {
  /** Position attribute from the pool (vec4 px,py,pz,age). */
  positionAttribute: unknown;
  /** Velocity attribute from the pool (vec4 vx,vy,vz,size). */
  velocityAttribute: unknown;
  /** Misc attribute from the pool (vec4 life,seed,startSize,startSpeed). */
  miscAttribute: unknown;
  /** 4-stop color/size ramp over normalized life. */
  ramp: ParticleRamp;
  /** Optional sprite texture (multiplied with the disc falloff). */
  map?: THREE.Texture | null;
  /** Blend mode. */
  blending?: THREE.Blending;
  /** Whether to write depth (default false). */
  depthWrite?: boolean;
  /** Soft round-edge softness in UV units (default 0.1). */
  softness?: number;
  /**
   * Orient the quad along the particle's VELOCITY instead of the camera's up
   * axis, and stretch it by this factor along that direction (default off).
   *
   * A plain camera-facing billboard puts the sprite's +Y along the SCREEN's up
   * axis, which is right only while the camera looks at the horizon. Rain is a
   * vertical streak sprite, so looking up rotated world-down away from
   * screen-down and every drop appeared to fall sideways (2026-09-14). With
   * this on, the sprite's +Y follows the velocity's direction on screen, so
   * streaks read correctly from any camera angle — and wind-slanted rain leans
   * the right way for free.
   *
   * Only useful for streak sprites; round puffs should leave it off.
   */
  velocityStretch?: number;
  /**
   * Per-particle size gate (render wave 1, 2026-09-17): `(worldCenter vec3 node) → float node`,
   * multiplied into the billboard size in the vertex stage. See GpuParticleConfig.sizeGate.
   */
  sizeGate?: ((worldCenter: unknown) => unknown) | null;
  /**
   * Six-way lit smoke (2026-09-15): two baked directional-response flipbooks
   * (see SixWaySmoke.ts). Replaces `map`; the sprite is lit by the material's
   * `sixWayUniforms` light + ambient and blended as COVERAGE (Normal), never
   * additively.
   */
  sixWay?: SixWayMaps | null;
}

export interface GpuParticleMaterial extends THREE.NodeMaterial {
  /** Mutable ramp-stop uniform nodes (update via setRamp). */
  /** Present only for a six-way material: the light it is lit by. */
  sixWayUniforms?: {
    lightDirWorld: ReturnType<typeof uniform>;
    lightColor: ReturnType<typeof uniform>;
    ambientColor: ReturnType<typeof uniform>;
    emissiveColor: ReturnType<typeof uniform>;
  };
  rampUniforms: {
    times: ReturnType<typeof uniform>;
    color0: ReturnType<typeof uniform>;
    color1: ReturnType<typeof uniform>;
    color2: ReturnType<typeof uniform>;
    color3: ReturnType<typeof uniform>;
    sizes: ReturnType<typeof uniform>;
  };
  setRamp(ramp: ParticleRamp): void;
}

/**
 * Evaluate a 4-stop ramp at parameter `t` using three smoothstep segments.
 * `stops` are the four value nodes (vec4 or float). `times` is a vec4 of the
 * four stop positions. Returns a node of the same kind as the stops.
 */
function evalRamp4(t: any, times: any, s0: any, s1: any, s2: any, s3: any): any {
  // Segment blends. Each smoothstep ramps 0→1 across its stop interval.
  const f01 = smoothstep(times.x, times.y, t);
  const f12 = smoothstep(times.y, times.z, t);
  const f23 = smoothstep(times.z, times.w, t);
  const a = mix(s0, s1, f01);
  const b = mix(a, s2, f12);
  return mix(b, s3, f23);
}

export function createGpuParticleMaterial(ctx: GpuParticleMaterialContext): GpuParticleMaterial {
  const mat = new THREE.NodeMaterial() as GpuParticleMaterial;
  mat.name = 'GpuParticleMaterial';
  mat.transparent = true;
  // Premultiplied blending: normal blending is (One, OneMinusSrcAlpha) — with
  // the default (SrcAlpha, OneMinusSrcAlpha) every soft edge was multiplied by
  // alpha twice and each sprite wore a dark rim (2026-09-11, "a shadow around
  // the rain"). THE ONE PREMULTIPLY (shader gaps program, 2026-09-29): with this
  // flag three r185 ALSO multiplies the output's rgb by alpha in the shader
  // (`NodeMaterial.setupOutput` → `premultiplyAlpha`), so the colour node below
  // returns STRAIGHT rgb — it used to premultiply too, and every sprite came out
  // rgb·α² (dark fringes on smoke, additive sparks dimmed by their own alpha).
  mat.premultipliedAlpha = true;
  mat.depthWrite = ctx.depthWrite ?? false;
  mat.depthTest = true;
  // Six-way smoke is COVERAGE: it attenuates what is behind it (Normal), a
  // spark is light added on top (Additive). Never additive for lit smoke.
  mat.blending = ctx.sixWay ? THREE.NormalBlending : (ctx.blending ?? THREE.AdditiveBlending);
  mat.side = THREE.DoubleSide;
  // An overlay: the surface behind a streak keeps its normal / velocity — the
  // quad's own normal is the EMITTER's, sideways to the ground, and GTAO drew
  // a light rectangle around every raindrop (post/mrtOverlay.ts, 2026-09-17).
  markMrtOverlay(mat);

  // ── Ramp uniforms ──
  const r = ctx.ramp;
  const uTimes = uniform(new THREE.Vector4(r.times[0], r.times[1], r.times[2], r.times[3]));
  const uColor0 = uniform(r.colors[0].clone());
  const uColor1 = uniform(r.colors[1].clone());
  const uColor2 = uniform(r.colors[2].clone());
  const uColor3 = uniform(r.colors[3].clone());
  const uSizes = uniform(new THREE.Vector4(r.sizes[0], r.sizes[1], r.sizes[2], r.sizes[3]));

  mat.rampUniforms = {
    times: uTimes, color0: uColor0, color1: uColor1,
    color2: uColor2, color3: uColor3, sizes: uSizes,
  };
  mat.setRamp = (ramp: ParticleRamp): void => {
    (uTimes.value as THREE.Vector4).set(ramp.times[0], ramp.times[1], ramp.times[2], ramp.times[3]);
    (uColor0.value as THREE.Vector4).copy(ramp.colors[0]);
    (uColor1.value as THREE.Vector4).copy(ramp.colors[1]);
    (uColor2.value as THREE.Vector4).copy(ramp.colors[2]);
    (uColor3.value as THREE.Vector4).copy(ramp.colors[3]);
    (uSizes.value as THREE.Vector4).set(ramp.sizes[0], ramp.sizes[1], ramp.sizes[2], ramp.sizes[3]);
  };

  // Storage attributes (vec4 nodes).
  const posAttr = ctx.positionAttribute as any;
  const velAttr = ctx.velocityAttribute as any;
  const miscAttr = ctx.miscAttribute as any;

  // Varyings passed to the fragment stage.
  const vColor = varyingProperty('vec4', 'vParticleColor');
  const stretch = ctx.velocityStretch ?? 0;

  // ── Six-way smoke: the light in the BILLBOARD basis + the life fraction ──
  const six = ctx.sixWay ?? null;
  const vLightLocal = six ? varyingProperty('vec3', 'vSixWayLight') : null;
  const vLife = six ? varyingProperty('float', 'vSixWayLife') : null;
  const uSixLightDir = six ? uniform(new THREE.Vector3(0.3, 0.8, 0.5)) : null;
  const uSixLightColor = six ? uniform(new THREE.Vector3(1, 1, 1)) : null;
  const uSixAmbient = six ? uniform(new THREE.Vector3(0.25, 0.27, 0.3)) : null;
  const uSixEmissive = six ? uniform(new THREE.Vector3(0, 0, 0)) : null;
  if (six && uSixLightDir && uSixLightColor && uSixAmbient && uSixEmissive) {
    mat.sixWayUniforms = { lightDirWorld: uSixLightDir, lightColor: uSixLightColor, ambientColor: uSixAmbient, emissiveColor: uSixEmissive };
  }

  // ── Vertex node ──
  mat.vertexNode = Fn(() => {
    const center = vec3(posAttr.x, posAttr.y, posAttr.z);
    const age = posAttr.w;
    const baseSize = velAttr.w;
    const life = miscAttr.x.max(float(0.0001));
    const t = clamp(age.div(life), float(0.0), float(1.0));

    // Size ramp × base size. Stop POSITIONS come from uTimes (shared with the
    // color ramp); the four stop VALUES are the scalar components of uSizes.
    const sizeMul = evalRamp4(t, uTimes, uSizes.x, uSizes.y, uSizes.z, uSizes.w);
    // Size gate (render wave 1): a sheltered raindrop collapses to a degenerate quad.
    const gate = ctx.sizeGate ? float(ctx.sizeGate(center) as never) : null;
    const size = gate ? baseSize.mul(sizeMul).mul(gate) : baseSize.mul(sizeMul);

    // View-space center.
    const viewCenter = cameraViewMatrix.mul(vec4(center, float(1.0)));

    // Quad corner. PlaneGeometry(1,1) verts span [-0.5,0.5], so corner×size
    // makes the billboard `size` units wide — matching the CPU renderer's
    // `_scale.set(size,size,size)` on the same unit plane (consistent visual
    // scale across backends). Dead particles have size 0 → degenerate quad.
    const corner = vec2(positionGeometry.x, positionGeometry.y);
    const velDir = vec3(velAttr.x, velAttr.y, velAttr.z);

    // Billboard: offset along view X/Y by corner × size. In view space the
    // camera right = +X, up = +Y, so this always faces the camera.
    //
    // VELOCITY-ALIGNED variant (streak sprites): the quad's +Y follows the
    // particle's velocity projected onto the screen, so a rain streak points
    // the way the drop actually travels no matter where the camera looks.
    let offsetView;
    let axisXNode: any = null, axisYNode: any = null;
    if (stretch > 0) {
      // Velocity in VIEW space (w = 0: a direction, not a point).
      const viewVel = cameraViewMatrix.mul(vec4(velDir, float(0.0))).xyz;
      const planar = vec2(viewVel.x, viewVel.y);
      const planarLen = planar.length();
      // Falling straight toward or away from the camera leaves no screen
      // direction to align to — keep the camera-up axis there, and blend in
      // over a small length so the flip is not visible.
      const axisY = mix(vec2(0.0, 1.0), planar.div(planarLen.max(float(1e-4))), smoothstep(float(0.0), float(0.25), planarLen));
      const axisYn = axisY.div(axisY.length().max(float(1e-4)));
      const axisX = vec2(axisYn.y, axisYn.x.negate()); // perpendicular, same handedness
      axisXNode = axisX; axisYNode = axisYn;
      const along = corner.y.mul(size).mul(float(stretch));
      const across = corner.x.mul(size);
      offsetView = vec4(
        axisX.x.mul(across).add(axisYn.x.mul(along)),
        axisX.y.mul(across).add(axisYn.y.mul(along)),
        float(0.0), float(0.0),
      );
    } else {
      offsetView = vec4(corner.x.mul(size), corner.y.mul(size), float(0.0), float(0.0));
    }
    // Six-way: view space IS the billboard basis (+X right, +Y up, +Z toward
    // the camera); a stretched quad rotates x/y onto its screen axes.
    if (six && vLightLocal && vLife && uSixLightDir) {
      const lView = cameraViewMatrix.mul(vec4(uSixLightDir, float(0.0))).xyz;
      if (stretch > 0 && axisXNode && axisYNode) {
        const lxy = vec2(lView.x, lView.y);
        vLightLocal.assign(vec3(lxy.dot(axisXNode), lxy.dot(axisYNode), lView.z));
      } else {
        vLightLocal.assign(lView);
      }
      vLife.assign(t);
    }
    const clip = cameraProjectionMatrix.mul(viewCenter.add(offsetView));

    // Color ramp over life → varying.
    const col = evalRamp4(t, uTimes, uColor0, uColor1, uColor2, uColor3);
    vColor.assign(col);

    return clip;
  })();

  // ── Fragment / color node ──
  const softness = ctx.softness ?? 0.1;
  mat.colorNode = Fn(() => {
    // Soft round disc from UV centered at 0.5.
    const d = uv().sub(vec2(0.5, 0.5)).mul(float(2.0)); // [-1,1]
    const r2 = d.dot(d);
    const disc = smoothstep(float(1.0), float(1.0).sub(float(softness * 2)), r2.sqrt());

    // `rgba` starts as the per-instance varying then is optionally multiplied
    // by the sprite texture; typed loosely (TSL node graph) like SplatMaterial.
    let rgba: any = vColor;
    if (six && vLightLocal && vLife && uSixLightColor && uSixAmbient && uSixEmissive) {
      // FLIPBOOK over the particle's life, two frames cross-faded (the same
      // frames and fraction for BOTH maps, so the lighting never tears).
      const cols = float(six.columns), rows = float(six.rows);
      const frames = float(six.columns * six.rows);
      const fFrac = vLife.mul(frames.sub(1)).clamp(0, frames.sub(1));
      const f0 = fFrac.floor(), f1 = f0.add(1).min(frames.sub(1)), w = fFrac.fract();
      const frameUv = (f: any): any => {
        const col = f.mod(cols), row = f.div(cols).floor();
        // frame 0 sits top-left in the PNG; uv v runs bottom-up
        return uv().add(vec2(col, rows.sub(1).sub(row))).div(vec2(cols, rows));
      };
      const a0 = texture(six.mapA, frameUv(f0)), a1 = texture(six.mapA, frameUv(f1));
      const b0 = texture(six.mapB, frameUv(f0)), b1 = texture(six.mapB, frameUv(f1));
      const A = mix(a0, a1, w), B = mix(b0, b1, w);
      const lit = sixWayLitNodes(A, B, vLightLocal);
      const shade = uSixLightColor.mul(lit.response).add(uSixAmbient.mul(lit.ambient));
      const rgb = rgba.xyz.mul(shade).add(uSixEmissive.mul(lit.emissive));
      const alphaSix = rgba.w.mul(lit.alpha);
      return vec4(rgb, alphaSix); // straight — three premultiplies (THE ONE PREMULTIPLY)
    }
    if (ctx.map) {
      const tex = texture(ctx.map, uv());
      rgba = vec4(rgba.xyz.mul(tex.xyz), rgba.w.mul(tex.w));
    }
    const alpha = rgba.w.mul(disc);

    // STRAIGHT rgb: `premultipliedAlpha = true` makes three multiply by alpha
    // once itself (THE ONE PREMULTIPLY above) — premultiplying here as well was α².
    return vec4(rgba.xyz, alpha);
  })();

  return mat;
}
